/*
///////////////////
  SHARED UTILITIES
  Loaded before cart-elements.js and product.js
//////////////////
*/

const isDev = window.Shopify?.designMode || location.hostname === 'localhost';

const CART_ROUTES = {
  add: '/cart/add.js',
  change: '/cart/change.js',
  get: '/cart.js',
};

// API request functions with error handling
const api = {
  post: async (url, data) => {
    try {
      return await axios.post(url, data);
    } catch (error) {
      if (isDev) console.error(`API POST error for ${url}:`, error);
      throw error;
    }
  },

  get: async (url) => {
    try {
      return await axios.get(url);
    } catch (error) {
      if (isDev) console.error(`API GET error for ${url}:`, error);
      throw error;
    }
  },

  change: async (endpoint, data) => {
    await api.post(endpoint, data);
    return api.get(CART_ROUTES.get);
  },
};

// DOM utilities with element caching
const dom = {
  query: (selector, parent = document) => parent.querySelector(selector),
  queryAll: (selector, parent = document) => parent.querySelectorAll(selector),

  cache: new Map(),

  get: (selector, useCache = true) => {
    if (useCache && dom.cache.has(selector)) {
      return dom.cache.get(selector);
    }
    const element = dom.query(selector);
    if (useCache && element) {
      dom.cache.set(selector, element);
    }
    return element;
  },

  toggleSpinner: (item, isVisible) => {
    if (item) {
      item.style.display = isVisible ? 'block' : 'none';
      const quantityInput = item
        .closest('.quantity-selector-wrapper')
        ?.querySelector('.global-cart-item-quantity');
      if (quantityInput) {
        quantityInput.style.display = isVisible ? 'none' : '';
      }
    }
  },
};

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Base class for interactive custom elements
class CustomHTMLElement extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.addEventListener('click', this.handleClick);
    this.handleKeydown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') this.handleClick(event);
    };
    this.addEventListener('keydown', this.handleKeydown);
  }

  // Attributes are set on connect: the spec forbids adding them in the constructor.
  connectedCallback() {
    if (!this.hasAttribute('tabindex')) this.tabIndex = 0;
    if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
  }

  handleClick(event) {
    event.preventDefault();
    this.onClick(event);
  }

  onClick(event) {
    // Override in subclasses
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
  }
}

// Global cart count updater
const updateGlobalCartCount = (itemCount) => {
  const cartQuantities = dom.queryAll('.global-cart-quantity');
  const count = itemCount.toString();

  cartQuantities.forEach((element) => {
    if (element.textContent !== count) {
      element.textContent = count;
    }
  });
};

// The control in `fresh` matching `active`: same id (e.g. the gift note), else same line item, same tag,
// same position among that tag (adjust-quantity-button 0 = minus, 1 = plus); the section heading if the item is gone.
const equivalentFocusTarget = (active, fresh) => {
  const lineItem = active.closest('[data-key]');
  const tag = active.tagName.toLowerCase();
  const index = lineItem
    ? [...lineItem.querySelectorAll(tag)].indexOf(active)
    : -1;
  // Matched by dataset, not an attribute selector: line item keys contain ':'.
  const freshItem = lineItem
    ? [...fresh.querySelectorAll('[data-key]')].find(
        (el) => el.dataset.key === lineItem.dataset.key,
      )
    : null;
  return (
    (active.id ? fresh.querySelector(`#${CSS.escape(active.id)}`) : null) ??
    freshItem?.querySelectorAll(tag)[index] ??
    fresh.querySelector('#cart-page-heading, #slide-over-title') ??
    document.getElementById('MainContent')
  );
};

// Fetch a rendered section and swap the element that exists in both the page and the response.
const refreshCartSection = async (sectionId, targetSelector) => {
  const { data } = await api.get(
    `${window.location.pathname}?section_id=${sectionId}`,
  );
  const fresh = new DOMParser()
    .parseFromString(data, 'text/html')
    .querySelector(targetSelector);
  const current = document.querySelector(targetSelector);
  if (!fresh || !current)
    throw new Error(`refresh target ${targetSelector} not found`);
  // The gift note is only saved on checkout submit, so carry the typed value across the swap.
  const note = current.querySelector('textarea[name="note"]')?.value;
  const focusTarget = current.contains(document.activeElement)
    ? equivalentFocusTarget(document.activeElement, fresh)
    : null;
  current.replaceWith(fresh);
  const freshNote = fresh.querySelector('textarea[name="note"]');
  if (note !== undefined && freshNote) freshNote.value = note;
  // Next frame: Alpine un-cloaks the re-rendered drawer asynchronously, and hidden elements can't take focus.
  if (focusTarget) requestAnimationFrame(() => focusTarget.focus());
  hideCartError();
};

const hideCartError = () =>
  document.querySelector('[data-cart-error]')?.classList.add('hidden');

// User-facing cart error. Text only; region lives in snippets/cart-notifications.liquid (rendered once in theme.liquid).
const showCartError = (message) => {
  const region = document.querySelector('[data-cart-error]');
  if (!region) return;
  region.classList.remove('hidden');
  const messageEl = region.querySelector('[data-cart-error-message]');
  // Clear, then write next frame, so screen readers re-announce a repeated identical message.
  messageEl.textContent = '';
  requestAnimationFrame(() => {
    messageEl.textContent =
      message ||
      window.cartStrings?.error ||
      'There was an error updating your cart.';
  });
};

document.addEventListener('click', (event) => {
  if (event.target.closest('[data-cart-error-dismiss]')) hideCartError();
});
